import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import { useMemo } from 'react';

import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useFacetedSearchCards } from '@sorare/core/src/hooks/search/useFacetedSearchCards';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import type { StackProps } from '@sorare/core/src/lib/algolia';
import { groupBy } from '@sorare/core/src/lib/arrays';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';

import { TokenContent } from './TokenContent/index.tsx';
import type {
  AlternativeStackedTokensByIdsQuery,
  AlternativeStackedTokensByIdsQueryVariables,
  Token_anyCard,
} from './__generated__/index.graphql.ts';

type Props = {
  card: Token_anyCard;
  forceMobileLayout?: boolean;
  forceDesktopLayout?: boolean;
  stack?: StackProps;
  onlyPrimary: boolean;
  hideDetails?: boolean;
} & Pick<
  ComponentProps<typeof TokenContent>,
  | 'onToggleCard'
  | 'onInfoClick'
  | 'hideOwner'
  | 'hideSorareUser'
  | 'selected'
  | 'selectionMode'
  | 'totalXpNeeded'
  | 'disableSportSpecific'
  | 'action'
  | 'galleryOwnerSlug'
  | 'forceShowActionButton'
>;

const cardFragment = gql`
  fragment Token_anyCard on AnyCardInterface {
    slug
    liveSingleSaleOffer @skip(if: $onlyPrimary) {
      id
      receiverSide {
        id
        amounts {
          ...MonetaryAmountFragment_monetaryAmount
        }
      }
    }
    ...TokenContent_anyCard
  }
  ${monetaryAmountFragment}
  ${TokenContent.fragments.anyCard}
` as TypedDocumentNode<Token_anyCard>;

const ALTERNATIVE_STACKED_TOKENS_BY_IDS_QUERY = gql`
  query AlternativeStackedTokensByIdsQuery(
    $slugs: [String!]!
    $onlyPrimary: Boolean = false
    $googlePlayStoreCountryCode: String
    $isAndroidApp: Boolean = false
    $isIosApp: Boolean = false
    $withCollectionPredictions: Boolean = false
  ) {
    anyCards(slugs: $slugs) {
      slug
      ...Token_anyCard
    }
  }
  ${cardFragment}
` as TypedDocumentNode<
  AlternativeStackedTokensByIdsQuery,
  AlternativeStackedTokensByIdsQueryVariables
>;

const ATTRIBUTES_TO_RETRIEVE = ['objectID', 'asset_id', 'sport', 'slug'];

export const Token = ({ card, stack, hideDetails = false, ...rest }: Props) => {
  const { algoliaCardIndexes } = useConfigContext();

  const stacked = stack && stack.count > 1;
  const skipLowestPriceLookup =
    !stacked ||
    !stack.algoliaDistinctKey ||
    !!card.liveSingleSaleOffer ||
    !!card.livePrimaryOffer;

  const { results: lowestPriceHits } = useFacetedSearchCards<{
    objectID: string;
    slug: string;
    sport: string;
  }>({
    index: algoliaCardIndexes['Cards On Sale Lowest Price'],
    distinct: false,
    facetFilters: `sale.distinct_key:"${stack?.algoliaDistinctKey}"`,
    attributesToRetrieve: ATTRIBUTES_TO_RETRIEVE,
    hitsPerPage: 20,
    params: stack?.params,
    skip: skipLowestPriceLookup,
  });
  const { isIosApp, isAndroidApp, googlePlayStoreCountryCode } =
    useIsMobileApp();

  const lowestPriceSlugs = useMemo(
    () => lowestPriceHits?.hits.map(hit => hit.slug).filter(Boolean) || [],
    [lowestPriceHits?.hits]
  );

  const { data } = useQuery(ALTERNATIVE_STACKED_TOKENS_BY_IDS_QUERY, {
    variables: {
      slugs: lowestPriceSlugs,
      isIosApp,
      isAndroidApp,
      googlePlayStoreCountryCode,
    },
    skip: !lowestPriceHits || lowestPriceHits.length === 0,
  });

  const actualCard = useMemo(() => {
    if (!lowestPriceHits || !data) {
      return card;
    }
    const cardsBySlug = groupBy(data.anyCards, t => t.slug);
    const alternativeHit = lowestPriceHits.hits.find(
      hit => cardsBySlug[hit.slug]?.[0]?.liveSingleSaleOffer
    );
    if (alternativeHit) {
      return cardsBySlug[alternativeHit.slug]?.[0] || card;
    }

    const alternativePrimaryBuyHit = lowestPriceHits.hits.find(
      hit => cardsBySlug[hit.slug]?.[0]?.livePrimaryOffer
    );
    if (alternativePrimaryBuyHit) {
      return cardsBySlug[alternativePrimaryBuyHit.slug]?.[0] || card;
    }
    return card;
  }, [card, data, lowestPriceHits]);

  const stackedTokensCount = useMemo(() => {
    if (!lowestPriceHits) return stack?.count || 1;
    const alreadySoldHits =
      data?.anyCards.filter(t => {
        if (t.liveSingleSaleOffer) {
          return false;
        }
        if (t?.livePrimaryOffer?.price && !t.tokenOwner) return false;
        return true;
      }).length || 0;

    return lowestPriceHits.nbHits - alreadySoldHits;
  }, [data?.anyCards, lowestPriceHits, stack?.count]);

  return (
    <TokenContent
      card={actualCard}
      stackedTokensCount={stackedTokensCount}
      hideDetails={hideDetails}
      {...rest}
    />
  );
};

Token.fragments = {
  anyCard: cardFragment,
};
